import { useEffect, useRef, useState } from 'react';

import { Check } from '@phosphor-icons/react';
import classNames from 'classnames';
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from 'motion/react';

import type { TipJarCopy, TipJarMilestone } from './TipJar.types';
import * as styles from './TipJarMeter.css';
import { getTipJarProgress } from './tipJar.utils';

const FILL_TRANSITION = { duration: 1.2, ease: [0.16, 1, 0.3, 1] } as const;

type TipJarMeterProps = {
  goalCents: number;
  raisedCents: number;
  supportersCount: number;
  milestones: TipJarMilestone[];
  formatAmount: (amountCents: number) => string;
  copy: Pick<TipJarCopy, 'ofGoal' | 'remaining' | 'funded' | 'supporters'>;
  isAnimated?: boolean;
};

export const TipJarMeter = ({
  goalCents,
  raisedCents,
  supportersCount,
  milestones,
  formatAmount,
  copy,
  isAnimated = false,
}: TipJarMeterProps) => {
  const meterRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(meterRef, { once: true, amount: 0.3 });
  const prefersReducedMotion = useReducedMotion();

  const raised = useMotionValue(isAnimated ? 0 : raisedCents);
  const supporters = useMotionValue(isAnimated ? 0 : supportersCount);
  const fillScale = useTransform(raised, (cents) => Math.min(cents / goalCents, 1));

  const [countedCents, setCountedCents] = useState(raised.get());
  const [countedSupporters, setCountedSupporters] = useState(supporters.get());
  useMotionValueEvent(raised, 'change', setCountedCents);
  useMotionValueEvent(supporters, 'change', setCountedSupporters);

  useEffect(() => {
    if (!isAnimated || prefersReducedMotion) {
      raised.jump(raisedCents);
      supporters.jump(supportersCount);
      return;
    }

    if (!isInView) return;

    const raisedAnimation = animate(raised, raisedCents, FILL_TRANSITION);
    const supportersAnimation = animate(supporters, supportersCount, FILL_TRANSITION);

    return () => {
      raisedAnimation.stop();
      supportersAnimation.stop();
    };
  }, [isAnimated, prefersReducedMotion, isInView, raised, supporters, raisedCents, supportersCount]);

  // Whole units while counting, so cents don't flicker past. The animation lands on a float, so the
  // last frame snaps to the real total instead of staying on the floored dollar.
  const settled = Math.abs(countedCents - raisedCents) < 1;
  const countedAmount = settled ? raisedCents : Math.floor(countedCents / 100) * 100;
  const { isFunded, remainingCents } = getTipJarProgress(goalCents, countedAmount);
  // Counting up from zero must not read "Be the first to tip" for a jar readers have already filled.
  const shownSupporters = supportersCount === 0 ? 0 : Math.max(1, Math.round(countedSupporters));

  return (
    <div ref={meterRef} className={styles.meter}>
      <p className={styles.amountLine}>
        <span className={styles.raised}>{formatAmount(countedAmount)}</span>
        <span className={styles.goal}>{copy.ofGoal(formatAmount(goalCents))}</span>
      </p>

      <div className={styles.track} aria-hidden>
        <motion.div className={styles.fill} style={{ scaleX: fillScale }} />
        {milestones
          .filter((milestone) => milestone.amount_cents < goalCents)
          .map((milestone) => (
            <span
              key={`${milestone.label}-${milestone.amount_cents}`}
              className={styles.tick}
              style={{ left: `${(milestone.amount_cents / goalCents) * 100}%` }}
            />
          ))}
      </div>

      <p className={styles.status}>
        <span>{copy.supporters(shownSupporters)}</span>
        <span aria-hidden>&middot;</span>
        {isFunded ? (
          <span className={styles.funded}>{copy.funded}</span>
        ) : (
          <span>{copy.remaining(formatAmount(remainingCents ?? 0))}</span>
        )}
      </p>

      {milestones.length > 0 && (
        <ul className={styles.milestones}>
          {milestones.map((milestone) => {
            const isReached = countedCents >= milestone.amount_cents;

            return (
              <li
                key={`${milestone.label}-${milestone.amount_cents}`}
                className={classNames(styles.milestone, isReached && styles.milestoneReached)}
              >
                {isReached && <Check size={12} weight="bold" aria-hidden />}
                <span className={styles.milestoneAmount}>{formatAmount(milestone.amount_cents)}</span>
                <span>{milestone.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
